import React from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import SiteSearch from '../SiteSearch'
import { Portal, space, useTransition, device, easing } from '@ticketswap/solar'
import { callAll } from '../../utils'

const duration = 200

const Container = styled.div`
  width: 100%;
`

const Overlay = styled.div`
  position: fixed;
  z-index: 2147483647; /* largest accepted z-index value as integer */
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  padding: ${space[16]};

  opacity: ${props => (props.state === 'entering' || props.state === 'entered' ? 1 : 0)};
  transition-duration: ${duration}ms;
  transition-timing-function: ${easing.easeOutCubic};
  transition-property: opacity;

  @media ${device.tablet} {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding-top: ${space[24]};
  }
`

function SearchOverlay(props) {
  const [on, setOn] = React.useState(false)
  const hide = () => setOn(false)
  const [state, mounted] = useTransition({
    in: on,
    timeout: duration,
  })

  function toggle() {
    setOn(on => !on)
  }

  React.useEffect(() => {
    const handleEscKeyPress = ({ keyCode }) => keyCode === 27 && hide()

    if (on) {
      document.addEventListener('keydown', handleEscKeyPress, false)
    }

    return () => {
      document.removeEventListener('keydown', handleEscKeyPress, false)
    }
  }, [on])

  const getToggleProps = (props = {}) => ({
    'aria-controls': 'target',
    'aria-expanded': on,
    ...props,
    onClick: callAll(props.onClick, toggle),
  })

  function getStateAndHelpers() {
    return {
      on,
      getToggleProps,
      toggle,
    }
  }

  return (
    <>
      {props.children(getStateAndHelpers())}
      <Portal>
        {mounted && (
          <Overlay aria-modal="true" onClick={hide} tabIndex="-1" state={state}>
            <Container
              onClick={e => e.stopPropagation()}
              onKeyDown={e => e.stopPropagation()}
              role="presentation"
            >
              <SiteSearch width="medium" defaultOpen={true} onClose={hide} />
            </Container>
          </Overlay>
        )}
      </Portal>
    </>
  )
}

SearchOverlay.propTypes = {
  defaultOn: PropTypes.bool,
  on: PropTypes.bool,
  onToggle: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.array]).isRequired,
}

export default SearchOverlay
